<script setup lang="ts">
import type { Approval, Comment, Project, Task, UploadedFile } from '~/types/models';
import { AlertTriangle, Archive, ArrowRight, Clock, Eye, File, LayoutPanelTop, MessageSquare, MoreHorizontal, Pencil, RotateCcw, Trash2 } from 'lucide-vue-next';
import EntityCardShell from '@/components/entity/EntityCardShell.vue';
import EntityActionButton from '@/components/entity/EntityActionButton.vue';
import ProjectMilestoneTimeline from '@/components/projects/ProjectMilestoneTimeline.vue';
import { Button } from '~/components/ui/button';
import { ContextMenuContent, ContextMenuItem, ContextMenuLabel, ContextMenuSeparator } from '~/components/ui/context-menu';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from '~/components/ui/dropdown-menu';
import { HoverCard, HoverCardContent, HoverCardTrigger } from '~/components/ui/hover-card';
import { Progress } from '~/components/ui/progress';
import ResponsiveActionSubmenu from '~/components/ui/responsive-action-submenu/ResponsiveActionSubmenu.vue';
import TagList from '@/components/entity/TagList.vue';
import { clientOwnershipLabel } from '~/lib/clientOwnership';
import { filterEntitiesForProjectScope } from '~/lib/projectEntityScope';
import { resolveProjectMilestones } from '~/lib/projectMilestones';
import { isProjectArchived, isProjectInactive } from '~/lib/projectLifecycle';
import { getProjectNoticeIconComponent } from '~/lib/projectNotice';
import { getProjectStatusOption, getTenantProjectStatuses } from '~/lib/projectConfig';
import { toast } from 'vue-sonner';

type ProjectCardStats = {
    taskTotal: number,
    taskCompleted: number,
    commentsCount: number,
    filesCount: number,
    pendingApprovals: number,
    requestedInfoTasks?: number,
    actionsRequired?: number,
};

interface Props {
    project: Project;
    tenantSlug?: string;
    href?: string;
    showClient?: boolean;
    showClientMedia?: boolean;
    clientAvatarUrl?: string;
    compact?: boolean;
    canMutate?: boolean;
    stats?: ProjectCardStats;
    tasks?: Task[];
    approvals?: Approval[];
    surface?: 'default' | 'embedded' | 'list';
}

const props = withDefaults(defineProps<Props>(), {
    tenantSlug: '',
    href: '',
    showClient: true,
    showClientMedia: undefined,
    clientAvatarUrl: '',
    compact: false,
    canMutate: false,
    stats: undefined,
    surface: 'default',
});

const emit = defineEmits<{
    (e: 'edit', project: Project): void,
    (e: 'notice', project: Project): void,
    (e: 'archive', project: Project): void,
    (e: 'restore', project: Project): void,
    (e: 'delete', project: Project): void,
    (e: 'publish', project: Project): void,
}>();

const { tenantPath } = useTenantPath();
const tenantStore = useTenantStore();
const projectStore = useProjectStore();
const taskStore = useTaskStore();
const commentsStore = useCommentsStore();
const filesStore = useFilesStore();
const approvalsStore = useApprovalsStore();
const clientStore = useClientStore();
const entityTagsStore = useEntityTagsStore();
const projectTags = computed(() => entityTagsStore.tagsFor('project', props.project.id));
const isArchived = computed(() => isProjectArchived(props.project.status));
const clientNameById = computed(() => Object.fromEntries(
    clientStore.clients.map((client) => [client.id, client.companyName || client.name || client.id]),
));
const clientLabel = computed(() => clientOwnershipLabel(props.project, {
    clientNameById: clientNameById.value,
}));
const shouldShowClientMedia = computed(() => props.showClientMedia ?? props.showClient);
const clientInitials = computed(() => {
    const source = clientLabel.value.trim();
    if (!source) return 'CL';

    const parts = source.split(/\s+/).filter(Boolean);
    return parts.slice(0, 2).map((part) => part.charAt(0)).join('').toUpperCase();
});

const projectInitials = computed(() => {
    const source = String(props.project.name || '').trim();
    if (!source) return 'PJ';

    return source
        .split(/\s+/)
        .filter(Boolean)
        .slice(0, 2)
        .map((part) => part.charAt(0))
        .join('')
        .toUpperCase();
});

const projectHref = computed(() => {
    if (props.href) return props.href;
    if (props.project.slug) return tenantPath(`/clients/projects/${props.project.slug}`);
    if (props.project.id) return tenantPath(`/projects/${props.project.id}`);
    return '';
});
const shellSubtitle = computed(() => (
    props.showClient ? clientLabel.value : props.project.stage || ''
));

const progress = computed(() => {
    if (!props.stats || props.stats.taskTotal <= 0) {
        return 0;
    }

    return Math.max(0, Math.min(Math.round((props.stats.taskCompleted / props.stats.taskTotal) * 100), 100));
});

const daysLeft = computed(() => {
    if (!props.project.dueDate) return null;
    const diff = new Date(props.project.dueDate).getTime() - Date.now();
    return Math.ceil(diff / (1000 * 60 * 60 * 24));
});

const daysLeftLabel = computed(() => {
    if (isProjectInactive(props.project.status)) return null;
    if (daysLeft.value === null) return null;
    if (daysLeft.value < 0) return `${Math.abs(daysLeft.value)} days overdue`;
    if (daysLeft.value === 0) return 'Due today';
    return `${daysLeft.value} Days left`;
});

const projectStatusOptions = computed(() => getTenantProjectStatuses(tenantStore.currentTenant));
const selectedStatusOption = computed(() => getProjectStatusOption(props.project.status, projectStatusOptions.value));
const projectStatusColor = computed(() => selectedStatusOption.value?.color || 'var(--primary)');
const projectStatusMenuItems = computed(() => projectStatusOptions.value.map(option => ({
    id: option.value,
    label: option.label,
    color: option.color || '#64748b',
})));

function hexToRgba(color: string, alpha: number) {
    const value = color.replace('#', '').trim();
    if (value.length !== 6) return color;
    const red = Number.parseInt(value.slice(0, 2), 16);
    const green = Number.parseInt(value.slice(2, 4), 16);
    const blue = Number.parseInt(value.slice(4, 6), 16);

    if ([red, green, blue].some(Number.isNaN)) {
        return color;
    }

    return `rgba(${red}, ${green}, ${blue}, ${alpha})`;
}

const statusStyle = computed(() => {
    const color = selectedStatusOption.value?.color || '#64748b';
    return {
        backgroundColor: hexToRgba(color, 0.08),
        color,
    };
});

const projectComments = computed<Comment[]>(() => commentsStore.commentsByEntity[`project:${props.project.id}`] || []);
const projectFiles = computed<UploadedFile[]>(() => filesStore.filesByEntity[`project:${props.project.id}`] || []);
const pendingApprovals = computed(() => (approvalsStore.approvalsByProject[props.project.id] || []).filter((approval) => approval.status === 'pending'));
const actionsRequiredCount = computed(() => props.stats?.actionsRequired ?? (
    (props.stats?.pendingApprovals ?? 0) + (props.stats?.requestedInfoTasks ?? 0)
));
const requestedInfoTasksCount = computed(() => props.stats?.requestedInfoTasks ?? 0);
const showInlineBadges = computed(() => props.surface !== 'list');
const noticePreview = computed(() => String(props.project.notice?.message || '').trim());
const noticeIconComponent = computed(() => getProjectNoticeIconComponent(props.project.notice?.icon));
const statusTextClass = computed(() => (
    props.surface === 'list'
        ? 'text-muted-foreground'
        : 'text-foreground'
));
const statusDotStyle = computed(() => ({
    backgroundColor: projectStatusColor.value,
}));
const orderedProjectStages = computed(() => resolveProjectMilestones(props.project.stages));
const hasProjectMilestones = computed(() => orderedProjectStages.value.length > 0);
const projectTasks = computed<Task[]>(() => (
    props.tasks
    ?? filterEntitiesForProjectScope(taskStore.tasks, props.project.id, props.project.clientId)
));
const projectApprovals = computed<Approval[]>(() => (
    props.approvals
    ?? filterEntitiesForProjectScope(
        approvalsStore.approvalsByProject[props.project.id] || [],
        props.project.id,
        props.project.clientId,
    )
));
const nextProjectStage = computed(() => {
    const stages = orderedProjectStages.value;
    if (stages.length === 0) return null;

    const currentStage = String(props.project.stage || '').trim().toLowerCase();
    const currentIndex = stages.findIndex((stage) => (
        String(stage.name || '').trim().toLowerCase() === currentStage
        || String(stage.id || '').trim().toLowerCase() === currentStage
    ));
    const nextIndex = currentIndex < 0 ? 0 : currentIndex + 1;
    return stages[nextIndex] || null;
});

const mediaFallbackClass = 'bg-muted text-muted-foreground';

function latestCommentPreview(comment: Comment) {
    const value = String(comment.content || '').trim();
    return value.length > 120 ? `${value.slice(0, 117)}...` : value;
}

async function quickUpdateProject(data: Partial<Project>, successMessage: string) {
    if (!props.canMutate || !props.project.id) return;

    try {
        await projectStore.updateProject(props.project.id, data);
        toast.success(successMessage);
    } catch (error: any) {
        toast.error(error?.message || 'Failed to update project.');
    }
}

async function moveToNextMilestone() {
    if (!nextProjectStage.value) return;
    await quickUpdateProject({ stage: nextProjectStage.value.name }, `Moved to ${nextProjectStage.value.name}.`);
}

function changeProjectStatus(item: { id: string }) {
    void quickUpdateProject({ status: item.id }, 'Project status updated.');
}
</script>

<template>
    <EntityCardShell
        :href="projectHref"
        :title="project.name"
        :subtitle="shellSubtitle"
        :image-url="project.profileImageURL || ''"
        :image-alt="`${project.name} preview`"
        :fallback-text="projectInitials"
        :fallback-class="mediaFallbackClass"
        :companion-image-url="shouldShowClientMedia ? (clientAvatarUrl || '') : ''"
        :companion-alt="clientLabel"
        :companion-fallback-text="shouldShowClientMedia ? clientInitials : ''"
        :compact="compact"
        :surface="surface"
        stretch-media
    >
        <template #compact-actions>
            <DropdownMenu>
                <DropdownMenuTrigger as-child>
                    <Button variant="ghost" size="icon" class="entity-row-action self-start" aria-label="More project actions">
                        <MoreHorizontal class="size-4" />
                        <span class="sr-only">Project actions</span>
                    </Button>
                </DropdownMenuTrigger>

                <DropdownMenuContent align="end" class="w-56">
                    <DropdownMenuItem v-if="projectHref" as-child>
                        <NuxtLink :to="projectHref" class="flex items-center gap-2">
                            <Eye class="size-4" />
                            View
                        </NuxtLink>
                    </DropdownMenuItem>

                    <template v-if="canMutate">
                        <DropdownMenuSeparator v-if="projectHref" />
                        <DropdownMenuItem
                            v-if="nextProjectStage"
                            class="flex items-center gap-2"
                            @click="moveToNextMilestone"
                        >
                            <ArrowRight class="size-4" />
                            Move to {{ nextProjectStage.name }}
                        </DropdownMenuItem>
                        <ResponsiveActionSubmenu
                            mode="dropdown"
                            label="Change status"
                            :icon="Pencil"
                            :items="projectStatusMenuItems"
                            @select="changeProjectStatus"
                        />
                        <DropdownMenuItem class="flex items-center gap-2" @click="emit('notice', project)">
                            <AlertTriangle class="size-4" />
                            Change notice
                        </DropdownMenuItem>
                        <DropdownMenuItem class="flex items-center gap-2" @click="emit('publish', project)">
                            <LayoutPanelTop class="size-4" />
                            {{ project.portalShared ? 'Update portal share' : 'Publish to client portal' }}
                        </DropdownMenuItem>
                        <DropdownMenuItem class="flex items-center gap-2" @click="emit('edit', project)">
                            <Pencil class="size-4" />
                            Edit
                        </DropdownMenuItem>
                        <DropdownMenuItem class="flex items-center gap-2" @click="emit(isArchived ? 'restore' : 'archive', project)">
                            <component :is="isArchived ? RotateCcw : Archive" class="size-4" />
                            {{ isArchived ? 'Restore' : 'Archive' }}
                        </DropdownMenuItem>
                        <DropdownMenuItem class="flex items-center gap-2 text-destructive focus:text-destructive" @click="emit('delete', project)">
                            <Trash2 class="size-4" />
                            Delete
                        </DropdownMenuItem>
                    </template>
                </DropdownMenuContent>
            </DropdownMenu>
        </template>

        <template #compact-body>
            <div class="space-y-2.5 text-xs text-muted-foreground">
                <div class="flex flex-wrap items-center gap-x-3 gap-y-2">
                    <TagList v-if="projectTags.length" :tags="projectTags" :colors="entityTagsStore.tagColors" :limit="2" compact show-icon />
                    <span class="inline-flex items-center gap-1.5" :class="statusTextClass">
                        <span class="h-2 w-2 rounded-full" :style="statusDotStyle" />
                        <span>{{ selectedStatusOption?.label || project.status || 'No status' }}</span>
                    </span>
                    <HoverCard v-if="noticePreview">
                        <HoverCardTrigger as-child>
                            <span class="inline-flex max-w-full items-center gap-1.5" :style="{ color: props.project.notice?.color || '#f59e0b' }" title="Project notice">
                                <component :is="noticeIconComponent" class="size-3.5 shrink-0" />
                                <span class="max-w-50 truncate">{{ noticePreview }}</span>
                            </span>
                        </HoverCardTrigger>
                        <HoverCardContent class="w-64">
                            <p class="text-xs font-medium text-foreground">Project notice</p>
                            <p class="mt-2 text-sm text-muted-foreground">{{ noticePreview }}</p>
                        </HoverCardContent>
                    </HoverCard>
                    <span v-if="project.stage" class="inline-flex items-center gap-1.5">
                        <LayoutPanelTop class="size-3.5 shrink-0" />
                        <span>{{ project.stage }}</span>
                    </span>
                    <HoverCard v-if="actionsRequiredCount > 0">
                        <HoverCardTrigger as-child>
                            <span class="inline-flex items-center gap-1.5" title="Actions required">
                                <AlertTriangle class="size-3.5 shrink-0 text-amber-600" />
                                <span>{{ actionsRequiredCount }}</span>
                            </span>
                        </HoverCardTrigger>
                        <HoverCardContent class="w-64">
                            <p class="text-xs font-medium text-foreground">Actions required</p>
                            <div class="mt-2 space-y-1 text-sm text-muted-foreground">
                                <p v-if="(stats?.pendingApprovals ?? 0) > 0">{{ stats?.pendingApprovals }} approval{{ stats?.pendingApprovals === 1 ? '' : 's' }} waiting</p>
                                <p v-if="requestedInfoTasksCount > 0">{{ requestedInfoTasksCount }} task{{ requestedInfoTasksCount === 1 ? '' : 's' }} need information</p>
                            </div>
                        </HoverCardContent>
                    </HoverCard>
                    <span v-if="(stats?.commentsCount ?? 0) > 0" class="inline-flex items-center gap-1.5" title="Comments">
                        <MessageSquare class="size-3.5 shrink-0" />
                        <span>{{ stats?.commentsCount ?? 0 }}</span>
                    </span>
                    <span v-if="(stats?.filesCount ?? 0) > 0" class="inline-flex items-center gap-1.5" title="Files">
                        <File class="size-3.5 shrink-0" />
                        <span>{{ stats?.filesCount ?? 0 }}</span>
                    </span>
                    <span v-if="(stats?.pendingApprovals ?? 0) > 0" class="inline-flex items-center gap-1.5" title="Pending approvals">
                        <AlertTriangle class="size-3.5 shrink-0 text-amber-600" />
                        <span>{{ stats?.pendingApprovals ?? 0 }}</span>
                    </span>
                    <span v-if="project.portalShared" class="inline-flex items-center gap-1.5" title="Shared to client portal">
                        <LayoutPanelTop class="size-3.5 shrink-0 text-sky-600" />
                        <span>Shared</span>
                    </span>
                    <span v-if="daysLeftLabel" class="inline-flex items-center gap-1.5">
                        <Clock class="size-3.5 shrink-0" />
                        <span>{{ daysLeftLabel }}</span>
                    </span>
                </div>
                <div
                    v-if="hasProjectMilestones || (stats?.taskTotal ?? 0) > 0"
                    class="flex min-w-0 w-full items-center gap-2"
                >
                    <ProjectMilestoneTimeline
                        v-if="hasProjectMilestones"
                        class="min-w-0 flex-1"
                        :stages="orderedProjectStages"
                        :tasks="projectTasks"
                        :approvals="projectApprovals"
                        :current-stage="project.stage || ''"
                        :aria-label="`${project.name} milestone progress`"
                        compact
                    />
                    <template v-else>
                        <Progress
                            :model-value="progress"
                            :indicator-color="projectStatusColor"
                            class="h-1.5 flex-1"
                        />
                        <span class="w-10 shrink-0 text-right text-xs tabular-nums text-muted-foreground">{{ progress }}%</span>
                    </template>
                </div>
            </div>
        </template>

        <template v-if="showInlineBadges" #badges>
            <span class="shrink-0 rounded-full px-3 py-1 text-xs font-medium" :style="statusStyle">
                {{ selectedStatusOption?.label || project.status || 'No status' }}
            </span>
            <HoverCard v-if="actionsRequiredCount > 0">
                <HoverCardTrigger as-child>
                    <span
                        class="inline-flex items-center gap-1.5 rounded-full border border-amber-400/60 bg-amber-500/15 px-2.5 py-1 text-[11px] font-medium leading-none text-amber-800 dark:text-amber-100"
                        title="Actions required"
                    >
                        <AlertTriangle class="size-3.5 shrink-0" />
                        <span>{{ actionsRequiredCount }} action{{ actionsRequiredCount === 1 ? '' : 's' }}</span>
                    </span>
                </HoverCardTrigger>
                <HoverCardContent class="w-64">
                    <p class="text-xs font-medium text-foreground">Actions required</p>
                    <div class="mt-2 space-y-1 text-sm text-muted-foreground">
                        <p v-if="(stats?.pendingApprovals ?? 0) > 0">{{ stats?.pendingApprovals }} approval{{ stats?.pendingApprovals === 1 ? '' : 's' }} waiting</p>
                        <p v-if="requestedInfoTasksCount > 0">{{ requestedInfoTasksCount }} task{{ requestedInfoTasksCount === 1 ? '' : 's' }} need information</p>
                    </div>
                </HoverCardContent>
            </HoverCard>
        </template>

        <template #mobile-actions>
            <DropdownMenu>
                <DropdownMenuTrigger as-child>
                    <Button variant="ghost" size="icon" class="entity-row-action self-start" aria-label="More project actions">
                        <MoreHorizontal class="size-4" />
                        <span class="sr-only">Project actions</span>
                    </Button>
                </DropdownMenuTrigger>

                <DropdownMenuContent align="end" class="w-56">
                    <DropdownMenuItem v-if="projectHref" as-child>
                        <NuxtLink :to="projectHref" class="flex items-center gap-2">
                            <Eye class="size-4" />
                            View
                        </NuxtLink>
                    </DropdownMenuItem>

                    <template v-if="canMutate">
                        <DropdownMenuSeparator v-if="projectHref" />
                        <DropdownMenuItem
                            v-if="nextProjectStage"
                            class="flex items-center gap-2"
                            @click="moveToNextMilestone"
                        >
                            <ArrowRight class="size-4" />
                            Move to {{ nextProjectStage.name }}
                        </DropdownMenuItem>
                        <ResponsiveActionSubmenu
                            mode="dropdown"
                            label="Change status"
                            :icon="Pencil"
                            :items="projectStatusMenuItems"
                            @select="changeProjectStatus"
                        />
                        <DropdownMenuItem class="flex items-center gap-2" @click="emit('notice', project)">
                            <AlertTriangle class="size-4" />
                            Change notice
                        </DropdownMenuItem>
                        <DropdownMenuItem class="flex items-center gap-2" @click="emit('publish', project)">
                            <LayoutPanelTop class="size-4" />
                            {{ project.portalShared ? 'Update portal share' : 'Publish to client portal' }}
                        </DropdownMenuItem>
                        <DropdownMenuItem class="flex items-center gap-2" @click="emit('edit', project)">
                            <Pencil class="size-4" />
                            Edit
                        </DropdownMenuItem>
                        <DropdownMenuItem class="flex items-center gap-2" @click="emit(isArchived ? 'restore' : 'archive', project)">
                            <component :is="isArchived ? RotateCcw : Archive" class="size-4" />
                            {{ isArchived ? 'Restore' : 'Archive' }}
                        </DropdownMenuItem>
                        <DropdownMenuItem class="flex items-center gap-2 text-destructive focus:text-destructive" @click="emit('delete', project)">
                            <Trash2 class="size-4" />
                            Delete
                        </DropdownMenuItem>
                    </template>
                </DropdownMenuContent>
            </DropdownMenu>
        </template>

        <template #actions>
            <EntityActionButton
                v-if="projectHref"
                label="View project"
                :icon="Eye"
                :href="projectHref"
            />
            <DropdownMenu>
                <DropdownMenuTrigger as-child>
                    <Button variant="ghost" size="icon" class="entity-row-action" aria-label="More project actions">
                        <MoreHorizontal class="size-4" />
                        <span class="sr-only">Project actions</span>
                    </Button>
                </DropdownMenuTrigger>

                <DropdownMenuContent align="end" class="w-56">
                    <DropdownMenuItem v-if="projectHref" as-child>
                        <NuxtLink :to="projectHref" class="flex items-center gap-2">
                            <Eye class="size-4" />
                            View
                        </NuxtLink>
                    </DropdownMenuItem>

                    <template v-if="canMutate">
                        <DropdownMenuSeparator v-if="projectHref" />
                        <DropdownMenuItem
                            v-if="nextProjectStage"
                            class="flex items-center gap-2"
                            @click="moveToNextMilestone"
                        >
                            <ArrowRight class="size-4" />
                            Move to {{ nextProjectStage.name }}
                        </DropdownMenuItem>
                        <DropdownMenuItem class="flex items-center gap-2" @click="emit('publish', project)">
                            <LayoutPanelTop class="size-4" />
                            {{ project.portalShared ? 'Update portal share' : 'Publish to client portal' }}
                        </DropdownMenuItem>
                        <DropdownMenuItem class="flex items-center gap-2" @click="emit('edit', project)">
                            <Pencil class="size-4" />
                            Edit
                        </DropdownMenuItem>
                        <DropdownMenuItem class="flex items-center gap-2" @click="emit(isArchived ? 'restore' : 'archive', project)">
                            <component :is="isArchived ? RotateCcw : Archive" class="size-4" />
                            {{ isArchived ? 'Restore' : 'Archive' }}
                        </DropdownMenuItem>
                        <DropdownMenuItem class="flex items-center gap-2 text-destructive focus:text-destructive" @click="emit('delete', project)">
                            <Trash2 class="size-4" />
                            Delete
                        </DropdownMenuItem>
                    </template>
                </DropdownMenuContent>
            </DropdownMenu>
        </template>

        <template #body>
            <div class="space-y-3">
                <div class="space-y-2.5 text-xs text-muted-foreground md:hidden">
                    <div class="flex flex-wrap items-center gap-2.5">
                        <TagList v-if="projectTags.length" :tags="projectTags" :colors="entityTagsStore.tagColors" :limit="2" compact show-icon />
                        <span class="inline-flex items-center gap-1.5" :class="statusTextClass">
                            <span class="h-2 w-2 rounded-full" :style="statusDotStyle" />
                            <span>{{ selectedStatusOption?.label || project.status || 'No status' }}</span>
                        </span>
                        <HoverCard v-if="noticePreview">
                            <HoverCardTrigger as-child>
                                <span class="inline-flex max-w-full items-center gap-1.5" :style="{ color: props.project.notice?.color || '#f59e0b' }" title="Project notice">
                                    <component :is="noticeIconComponent" class="size-3.5 shrink-0" />
                                    <span class="max-w-50 truncate">{{ noticePreview }}</span>
                                </span>
                            </HoverCardTrigger>
                            <HoverCardContent class="w-64">
                                <p class="text-xs font-medium text-foreground">Project notice</p>
                                <p class="mt-2 text-sm text-muted-foreground">{{ noticePreview }}</p>
                            </HoverCardContent>
                        </HoverCard>
                        <span v-if="project.stage" class="inline-flex items-center gap-1.5">
                            <LayoutPanelTop class="size-3.5 shrink-0" />
                            <span>{{ project.stage }}</span>
                        </span>
                        <span v-if="daysLeftLabel" class="inline-flex items-center gap-1.5">
                            <Clock class="size-3.5 shrink-0" />
                            {{ daysLeftLabel }}
                        </span>
                        <HoverCard v-if="project.portalShared">
                            <HoverCardTrigger as-child>
                                <span class="inline-flex items-center gap-1.5" title="Shared to client portal">
                                    <LayoutPanelTop class="size-3.5 text-sky-600" />
                                    <span>Shared</span>
                                </span>
                            </HoverCardTrigger>
                            <HoverCardContent class="w-64">
                                <p class="text-xs font-medium text-foreground">Shared to client portal</p>
                                <p class="mt-2 text-sm text-muted-foreground">{{ project.portalSummary || 'This project is visible to the client in their portal.' }}</p>
                            </HoverCardContent>
                        </HoverCard>
                        <span v-if="actionsRequiredCount > 0" class="inline-flex items-center gap-1.5" title="Actions required">
                            <AlertTriangle class="size-3.5 shrink-0 text-amber-600" />
                            <span>{{ actionsRequiredCount }}</span>
                        </span>
                    </div>

                    <div
                        v-if="hasProjectMilestones || (stats?.taskTotal ?? 0) > 0"
                        class="flex min-w-0 w-full items-center gap-2"
                    >
                        <ProjectMilestoneTimeline
                            v-if="hasProjectMilestones"
                            class="min-w-0 flex-1"
                            :stages="orderedProjectStages"
                            :tasks="projectTasks"
                            :approvals="projectApprovals"
                            :current-stage="project.stage || ''"
                            :aria-label="`${project.name} milestone progress`"
                            compact
                        />
                        <template v-else>
                            <Progress
                                :model-value="progress"
                                :indicator-color="projectStatusColor"
                                class="h-1.5 flex-1"
                            />
                            <span class="w-10 shrink-0 text-right text-xs tabular-nums text-muted-foreground">{{ progress }}%</span>
                        </template>
                    </div>

                    <div class="flex flex-wrap items-center gap-3">
                        <span class="inline-flex items-center gap-1.5" title="Comments">
                            <MessageSquare class="size-3.5" />
                            {{ stats?.commentsCount ?? 0 }}
                        </span>
                        <span class="inline-flex items-center gap-1.5" title="Files">
                            <File class="size-3.5" />
                            {{ stats?.filesCount ?? 0 }}
                        </span>
                        <span class="inline-flex items-center gap-1.5" title="Pending approvals">
                            <AlertTriangle class="size-3.5" :class="(stats?.pendingApprovals ?? 0) > 0 ? 'text-amber-600' : ''" />
                            {{ stats?.pendingApprovals ?? 0 }}
                        </span>
                    </div>
                </div>

                <div class="hidden items-center justify-end gap-x-4 text-xs text-muted-foreground md:flex md:flex-wrap">
                    <TagList v-if="projectTags.length" :tags="projectTags" :colors="entityTagsStore.tagColors" :limit="2" compact show-icon />
                    <span class="inline-flex items-center gap-1.5" :class="statusTextClass">
                        <span class="h-2 w-2 rounded-full" :style="statusDotStyle" />
                        <span>{{ selectedStatusOption?.label || project.status || 'No status' }}</span>
                    </span>
                    <HoverCard v-if="noticePreview">
                        <HoverCardTrigger as-child>
                            <span class="inline-flex max-w-full items-center gap-1.5" :style="{ color: props.project.notice?.color || '#f59e0b' }" title="Project notice">
                                <component :is="noticeIconComponent" class="size-3.5 shrink-0" />
                                <span class="max-w-50 truncate">{{ noticePreview }}</span>
                            </span>
                        </HoverCardTrigger>
                        <HoverCardContent class="w-64">
                            <p class="text-xs font-medium text-foreground">Project notice</p>
                            <p class="mt-2 text-sm text-muted-foreground">{{ noticePreview }}</p>
                        </HoverCardContent>
                    </HoverCard>
                    <span v-if="project.stage" class="inline-flex items-center gap-1.5">
                        <LayoutPanelTop class="size-3.5 shrink-0" />
                        <span>{{ project.stage }}</span>
                    </span>
                    <HoverCard v-if="actionsRequiredCount > 0">
                        <HoverCardTrigger as-child>
                            <span class="inline-flex items-center gap-1.5" title="Actions required">
                                <AlertTriangle class="size-3.5 shrink-0 text-amber-600" />
                                <span>{{ actionsRequiredCount }}</span>
                            </span>
                        </HoverCardTrigger>
                        <HoverCardContent class="w-64">
                            <p class="text-xs font-medium text-foreground">Actions required</p>
                            <div class="mt-2 space-y-1 text-sm text-muted-foreground">
                                <p v-if="(stats?.pendingApprovals ?? 0) > 0">{{ stats?.pendingApprovals }} approval{{ stats?.pendingApprovals === 1 ? '' : 's' }} waiting</p>
                                <p v-if="requestedInfoTasksCount > 0">{{ requestedInfoTasksCount }} task{{ requestedInfoTasksCount === 1 ? '' : 's' }} need information</p>
                            </div>
                        </HoverCardContent>
                    </HoverCard>
                    <span v-if="(stats?.commentsCount ?? 0) > 0" class="inline-flex items-center gap-1.5" title="Comments">
                        <MessageSquare class="size-3.5 shrink-0" />
                        <span>{{ stats?.commentsCount ?? 0 }}</span>
                    </span>
                    <span v-if="(stats?.filesCount ?? 0) > 0" class="inline-flex items-center gap-1.5" title="Files">
                        <File class="size-3.5 shrink-0" />
                        <span>{{ stats?.filesCount ?? 0 }}</span>
                    </span>
                    <span v-if="(stats?.pendingApprovals ?? 0) > 0" class="inline-flex items-center gap-1.5" title="Pending approvals">
                        <AlertTriangle class="size-3.5 shrink-0 text-amber-600" />
                        <span>{{ stats?.pendingApprovals ?? 0 }}</span>
                    </span>
                    <span v-if="project.portalShared" class="inline-flex items-center gap-1.5" title="Shared to client portal">
                        <LayoutPanelTop class="size-3.5 shrink-0 text-sky-600" />
                        <span>Shared</span>
                    </span>
                    <span v-if="daysLeftLabel" class="inline-flex items-center gap-1.5">
                        <Clock class="size-3.5 shrink-0" />
                        <span>{{ daysLeftLabel }}</span>
                    </span>
                </div>

                <div
                    v-if="hasProjectMilestones || (stats?.taskTotal ?? 0) > 0"
                    class="hidden items-center gap-3 text-xs text-muted-foreground md:flex"
                >
                    <div class="flex flex-1 items-center gap-2">
                        <ProjectMilestoneTimeline
                            v-if="hasProjectMilestones"
                            class="min-w-0 flex-1"
                            :stages="orderedProjectStages"
                            :tasks="projectTasks"
                            :approvals="projectApprovals"
                            :current-stage="project.stage || ''"
                            :aria-label="`${project.name} milestone progress`"
                            compact
                        />
                        <template v-else>
                            <Progress
                                :model-value="progress"
                                :indicator-color="projectStatusColor"
                                class="h-1.5 flex-1"
                            />
                            <span class="w-10 shrink-0 text-right text-xs tabular-nums text-muted-foreground">{{ progress }}%</span>
                        </template>
                    </div>
                </div>
            </div>
        </template>

        <template #context-menu>
            <ContextMenuContent v-if="canMutate" class="w-56">
            <ContextMenuLabel>Project</ContextMenuLabel>
            <ContextMenuSeparator />
            <ContextMenuItem v-if="projectHref" @select="projectHref && navigateTo(projectHref)">
                <Eye class="size-4" />
                Open project
            </ContextMenuItem>
            <ResponsiveActionSubmenu
                mode="context"
                label="Change status"
                :icon="Pencil"
                :items="projectStatusMenuItems"
                @select="changeProjectStatus"
            />
            <ContextMenuItem @select="emit('notice', project)">
                <AlertTriangle class="size-4" />
                Change notice
            </ContextMenuItem>
            <ContextMenuItem v-if="nextProjectStage" @select="moveToNextMilestone">
                <ArrowRight class="size-4" />
                Move to {{ nextProjectStage.name }}
            </ContextMenuItem>
            <ContextMenuSeparator />
            <ContextMenuItem @select="emit('publish', project)">
                <LayoutPanelTop class="size-4" />
                {{ project.portalShared ? 'Update portal share' : 'Publish to client portal' }}
            </ContextMenuItem>
            <ContextMenuItem @select="emit('edit', project)">
                <Pencil class="size-4" />
                Edit project
            </ContextMenuItem>
            <ContextMenuItem @select="emit(isArchived ? 'restore' : 'archive', project)">
                <component :is="isArchived ? RotateCcw : Archive" class="size-4" />
                {{ isArchived ? 'Restore project' : 'Archive project' }}
            </ContextMenuItem>
            <ContextMenuItem class="text-destructive focus:text-destructive" @select="emit('delete', project)">
                <Trash2 class="size-4" />
                Delete project
            </ContextMenuItem>
            </ContextMenuContent>
        </template>
    </EntityCardShell>
</template>
